import { useEffect, useMemo, useState } from "react";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import Swal from "sweetalert2";
import { db, storage } from "../../firebase";

const ROLE_OPTIONS = [
  { key: "librarian", label: "Бібліотекар" },
  { key: "maister", label: "Майстер" },
  { key: "business", label: "Бізнес-партнер" },
];

const FIELD_TYPES = [
  { value: "text", label: "Короткий текст" },
  { value: "textarea", label: "Довгий текст" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Телефон" },
  { value: "url", label: "Посилання" },
  { value: "number", label: "Число" },
  { value: "select", label: "Випадаючий список" },
  { value: "radio", label: "Один варіант" },
  { value: "checkbox", label: "Кілька варіантів" },
  { value: "file", label: "Файл" },
];

const EMPTY_FORM = {
  title: "",
  description: "",
  warningText:
    "У разі якщо користувач не заповнив форму, його заявка на зміну кабінету розглядатись не буде.",
  bannerUrl: "",
  isActive: true,
  fields: [],
};

const DEFAULT_FIELDS = {
  librarian: [
    { id: "library_name", type: "text", label: "Назва бібліотеки", placeholder: "", helpText: "", required: true, options: [], order: 1 },
    { id: "responsible_name", type: "text", label: "ФІО відповідального", placeholder: "", helpText: "", required: true, options: [], order: 2 },
    { id: "position", type: "text", label: "Посада", placeholder: "", helpText: "", required: false, options: [], order: 3 },
    { id: "phone", type: "phone", label: "Телефон", placeholder: "+380...", helpText: "", required: true, options: [], order: 4 },
    { id: "email", type: "email", label: "Email", placeholder: "", helpText: "", required: true, options: [], order: 5 },
    { id: "address", type: "textarea", label: "Адреса бібліотеки", placeholder: "", helpText: "", required: true, options: [], order: 6 },
    { id: "social_links", type: "textarea", label: "Сайт або соцмережі", placeholder: "", helpText: "", required: false, options: [], order: 7 },
  ],
  maister: [
    { id: "full_name", type: "text", label: "ФІО", placeholder: "", helpText: "", required: true, options: [], order: 1 },
    { id: "short_name", type: "text", label: "Як коротко вас представляти", placeholder: "", helpText: "", required: false, options: [], order: 2 },
    { id: "phone", type: "phone", label: "Телефон", placeholder: "+380...", helpText: "", required: true, options: [], order: 3 },
    { id: "email", type: "email", label: "Email", placeholder: "", helpText: "", required: true, options: [], order: 4 },
    { id: "about_master", type: "textarea", label: "Про Майстра", placeholder: "", helpText: "", required: true, options: [], order: 5 },
    { id: "key_idea", type: "textarea", label: "Ключова думка", placeholder: "", helpText: "", required: false, options: [], order: 6 },
    { id: "social_links", type: "textarea", label: "Соцмережі / сайт / портфоліо", placeholder: "", helpText: "", required: false, options: [], order: 7 },
  ],
  business: [
    { id: "company_name", type: "text", label: "Назва компанії / ФОП", placeholder: "", helpText: "", required: true, options: [], order: 1 },
    { id: "contact_person", type: "text", label: "Контактна особа", placeholder: "", helpText: "", required: true, options: [], order: 2 },
    { id: "phone", type: "phone", label: "Телефон", placeholder: "+380...", helpText: "", required: true, options: [], order: 3 },
    { id: "email", type: "email", label: "Email", placeholder: "", helpText: "", required: true, options: [], order: 4 },
    { id: "business_type", type: "select", label: "Тип бізнесу", placeholder: "", helpText: "", required: true, options: ["Книгарня", "Інтернет-магазин", "Школа", "Дитячий центр", "Інше"], order: 5 },
    { id: "comment", type: "textarea", label: "Коментар для адміністратора", placeholder: "", helpText: "", required: false, options: [], order: 6 },
  ],
};

const styles = {
  wrap: { padding: 24, width: "100%" },
  title: { margin: "0 0 18px", fontSize: 28 },
  card: {
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: 14,
    padding: 18,
    marginBottom: 18,
    boxShadow: "0 8px 24px rgba(0,0,0,.04)",
  },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 },
  label: { display: "block", fontWeight: 700, marginBottom: 7 },
  input: {
    width: "100%",
    border: "1px solid #d7d7d7",
    borderRadius: 9,
    padding: "10px 12px",
    fontSize: 15,
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    border: "1px solid #d7d7d7",
    borderRadius: 9,
    padding: "10px 12px",
    fontSize: 15,
    minHeight: 90,
    boxSizing: "border-box",
  },
  button: {
    border: "none",
    background: "#3e51c1",
    color: "#fff",
    borderRadius: 9,
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 700,
  },
  buttonLight: {
    border: "1px solid #ccc",
    background: "#fff",
    color: "#222",
    borderRadius: 9,
    padding: "10px 14px",
    cursor: "pointer",
  },
  buttonDanger: {
    border: "none",
    background: "#c62828",
    color: "#fff",
    borderRadius: 9,
    padding: "9px 12px",
    cursor: "pointer",
  },
  fieldCard: { border: "1px solid #eee", borderRadius: 12, padding: 14, marginBottom: 14 },
  actions: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
  banner: { maxWidth: 420, width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 12, marginTop: 10 },
};

const makeFieldId = () => `field_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const roleLabelByKey = (key) => ROLE_OPTIONS.find((item) => item.key === key)?.label || key;

const sortFields = (fields = []) =>
  [...fields].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));

const normalizeOptionsText = (options = []) =>
  Array.isArray(options) ? options.join("\n") : String(options || "");

const parseOptions = (text) =>
  String(text || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

export default function RoleFormsAdmin() {
  const [roleKey, setRoleKey] = useState("librarian");
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);

  const selectedRoleLabel = useMemo(() => roleLabelByKey(roleKey), [roleKey]);
  const fields = useMemo(() => sortFields(form.fields), [form.fields]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, "roleForms", roleKey));
        if (!alive) return;

        if (snap.exists()) {
          setForm({ ...EMPTY_FORM, ...snap.data(), fields: sortFields(snap.data().fields || []) });
        } else {
          setForm({
            ...EMPTY_FORM,
            roleKey,
            roleLabel: selectedRoleLabel,
            title: `Анкета: ${selectedRoleLabel}`,
            fields: DEFAULT_FIELDS[roleKey] || [],
          });
        }
        setBannerFile(null);
      } catch (error) {
        console.error("Помилка завантаження форми:", error);
        Swal.fire("Помилка", "Не вдалося завантажити форму.", "error");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, [roleKey, selectedRoleLabel]);

  const setFormValue = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateField = (fieldId, key, value) => {
    setForm((prev) => ({
      ...prev,
      fields: (prev.fields || []).map((field) =>
        field.id === fieldId ? { ...field, [key]: value } : field,
      ),
    }));
  };

  const addField = () => {
    setForm((prev) => {
      const nextOrder = (prev.fields || []).length + 1;
      return {
        ...prev,
        fields: [
          ...(prev.fields || []),
          {
            id: makeFieldId(),
            type: "text",
            label: "Нове поле",
            placeholder: "",
            helpText: "",
            required: false,
            options: [],
            order: nextOrder,
          },
        ],
      };
    });
  };

  const removeField = (fieldId) => {
    if (!window.confirm("Видалити це поле з форми?")) return;
    setForm((prev) => ({
      ...prev,
      fields: (prev.fields || []).filter((field) => field.id !== fieldId),
    }));
  };

  const moveField = (fieldId, direction) => {
    setForm((prev) => {
      const list = sortFields(prev.fields || []);
      const index = list.findIndex((field) => field.id === fieldId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= list.length) return prev;
      const copy = [...list];
      const [item] = copy.splice(index, 1);
      copy.splice(nextIndex, 0, item);
      return {
        ...prev,
        fields: copy.map((field, idx) => ({ ...field, order: idx + 1 })),
      };
    });
  };

  const uploadBannerIfNeeded = async () => {
    if (!bannerFile) return form.bannerUrl || "";
    const safeName = String(bannerFile.name || "banner").replace(/[^a-zA-Z0-9а-яА-ЯіІїЇєЄґҐ._-]/g, "_");
    const bannerRef = ref(storage, `role-forms/${roleKey}/banner-${Date.now()}-${safeName}`);
    await uploadBytes(bannerRef, bannerFile, { contentType: bannerFile.type || undefined });
    return getDownloadURL(bannerRef);
  };

  const saveForm = async () => {
    if (!form.title.trim()) {
      Swal.fire("Увага", "Вкажіть назву форми.", "info");
      return;
    }

    const invalidField = (form.fields || []).find((field) => !String(field.label || "").trim());
    if (invalidField) {
      Swal.fire("Увага", "Усі поля мають мати назву.", "info");
      return;
    }

    setSaving(true);

    try {
      const bannerUrl = await uploadBannerIfNeeded();
      const payload = {
        ...form,
        roleKey,
        roleLabel: selectedRoleLabel,
        bannerUrl,
        fields: sortFields(form.fields || []).map((field, index) => ({
          ...field,
          id: field.id || makeFieldId(),
          type: field.type || "text",
          label: String(field.label || "").trim(),
          placeholder: String(field.placeholder || ""),
          helpText: String(field.helpText || ""),
          required: Boolean(field.required),
          options: Array.isArray(field.options) ? field.options : [],
          order: index + 1,
        })),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "roleForms", roleKey), payload, { merge: true });
      setForm(payload);
      setBannerFile(null);
      Swal.fire("Готово", "Форму збережено.", "success");
    } catch (error) {
      console.error("Помилка збереження форми:", error);
      Swal.fire("Помилка", "Не вдалося зберегти форму.", "error");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    if (!window.confirm("Замінити поля на базовий шаблон для цієї ролі?")) return;
    setForm((prev) => ({
      ...prev,
      title: prev.title || `Анкета: ${selectedRoleLabel}`,
      fields: DEFAULT_FIELDS[roleKey] || [],
    }));
  };

  const fieldNeedsOptions = (type) => ["select", "radio", "checkbox"].includes(type);

  return (
    <div style={styles.wrap}>
      <h2 style={styles.title}>Форми зміни кабінету</h2>

      <div style={styles.card}>
        <label style={styles.label}>Роль</label>
        <select style={styles.input} value={roleKey} onChange={(event) => setRoleKey(event.target.value)}>
          {ROLE_OPTIONS.map((role) => (
            <option key={role.key} value={role.key}>
              {role.label}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.card}>
        {loading ? <p>Завантаження...</p> : null}

        <div style={styles.grid}>
          <div>
            <label style={styles.label}>Назва форми</label>
            <input style={styles.input} value={form.title || ""} onChange={(event) => setFormValue("title", event.target.value)} />
          </div>
          <div>
            <label style={styles.label}>Статус</label>
            <select
              style={styles.input}
              value={form.isActive === false ? "inactive" : "active"}
              onChange={(event) => setFormValue("isActive", event.target.value === "active")}
            >
              <option value="active">Активна</option>
              <option value="inactive">Неактивна</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={styles.label}>Опис форми</label>
          <textarea style={styles.textarea} value={form.description || ""} onChange={(event) => setFormValue("description", event.target.value)} />
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={styles.label}>Попередження перед заповненням</label>
          <textarea style={styles.textarea} value={form.warningText || ""} onChange={(event) => setFormValue("warningText", event.target.value)} />
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={styles.label}>Банер форми</label>
          <input style={styles.input} type="file" accept="image/*" onChange={(event) => setBannerFile(event.target.files?.[0] || null)} />
          {form.bannerUrl ? <img src={form.bannerUrl} alt="Банер форми" style={styles.banner} /> : null}
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ ...styles.actions, justifyContent: "space-between", marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Поля форми</h3>
          <div style={styles.actions}>
            <button type="button" style={styles.buttonLight} onClick={resetToDefault}>
              Базовий шаблон
            </button>
            <button type="button" style={styles.button} onClick={addField}>
              Додати поле
            </button>
          </div>
        </div>

        {fields.length === 0 ? <p>Поля ще не додані.</p> : null}

        {fields.map((field, index) => (
          <div key={field.id} style={styles.fieldCard}>
            <div style={{ ...styles.actions, justifyContent: "space-between", marginBottom: 12 }}>
              <strong>Поле #{index + 1}</strong>
              <div style={styles.actions}>
                <button type="button" style={styles.buttonLight} onClick={() => moveField(field.id, -1)}>
                  ↑
                </button>
                <button type="button" style={styles.buttonLight} onClick={() => moveField(field.id, 1)}>
                  ↓
                </button>
                <button type="button" style={styles.buttonDanger} onClick={() => removeField(field.id)}>
                  Видалити
                </button>
              </div>
            </div>

            <div style={styles.grid}>
              <div>
                <label style={styles.label}>Тип поля</label>
                <select style={styles.input} value={field.type || "text"} onChange={(event) => updateField(field.id, "type", event.target.value)}>
                  {FIELD_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={styles.label}>Назва поля</label>
                <input style={styles.input} value={field.label || ""} onChange={(event) => updateField(field.id, "label", event.target.value)} />
              </div>

              <div>
                <label style={styles.label}>Placeholder</label>
                <input style={styles.input} value={field.placeholder || ""} onChange={(event) => updateField(field.id, "placeholder", event.target.value)} />
              </div>

              <div>
                <label style={styles.label}>Обовʼязкове</label>
                <select
                  style={styles.input}
                  value={field.required ? "yes" : "no"}
                  onChange={(event) => updateField(field.id, "required", event.target.value === "yes")}
                >
                  <option value="yes">Так</option>
                  <option value="no">Ні</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={styles.label}>Підказка під полем</label>
              <input style={styles.input} value={field.helpText || ""} onChange={(event) => updateField(field.id, "helpText", event.target.value)} />
            </div>

            {fieldNeedsOptions(field.type) ? (
              <div style={{ marginTop: 12 }}>
                <label style={styles.label}>Варіанти відповіді, кожен з нового рядка</label>
                <textarea
                  style={styles.textarea}
                  value={normalizeOptionsText(field.options)}
                  onChange={(event) => updateField(field.id, "options", parseOptions(event.target.value))}
                />
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div style={styles.actions}>
        <button type="button" style={styles.button} onClick={saveForm} disabled={saving}>
          {saving ? "Зберігаємо..." : "Зберегти форму"}
        </button>
      </div>
    </div>
  );
}
